import { ReactElement, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { Grid, Stack, Typography } from '@mui/material'
import { Checkbox, Select, TextField, UnitConvertedField } from '@ui/forms'
import { seismicSValueFormatter } from 'src/utils/numberFormatters'
import {
  SIA_groundTypeCombinationOptions,
  groundTypeCombinationOptions,
  seismicStandardOptions,
  seismicZoneOptions,
  SIA_seismicZoneOptions,
  getDIN_EN_1998_S,
} from '../constants'

const SeismicBaseParameters = (): ReactElement => {
  const { watch } = useFormContext()

  const seismicStandard: SeismicStandard = watch('standard_used')
  const spectralAcceleration = watch('spectral_acceleration') || 0

  const DIN_EN_1998_groundTypeCombinationOptionsWithCalculatedSValue = useMemo(
    () =>
      groundTypeCombinationOptions.map(option => {
        const sValueAtOption = getDIN_EN_1998_S(spectralAcceleration, option.value)
        const sValueAtOptionFormatted = seismicSValueFormatter(sValueAtOption)
        return { value: option.value, label: `${option.label} | S = ${sValueAtOptionFormatted}` }
      }),
    [spectralAcceleration],
  )

  const groundTypeCombinationSelectOptions = useMemo(() => {
    switch (seismicStandard) {
      case 'SIA_261':
        return SIA_groundTypeCombinationOptions
      case 'DIN_EN_1998':
        return DIN_EN_1998_groundTypeCombinationOptionsWithCalculatedSValue
      default:
        return groundTypeCombinationOptions
    }
  }, [seismicStandard, DIN_EN_1998_groundTypeCombinationOptionsWithCalculatedSValue])

  return (
    <Grid container columnSpacing={1} rowSpacing={2} alignItems="center">
      <Grid item xs={6}>
        <Select label="Norm" name="standard_used" options={seismicStandardOptions} />
      </Grid>

      <Grid item xs={6}>
        <Select
          label="Untergrundverh."
          name="ground_type_combination"
          options={groundTypeCombinationSelectOptions}
        />
      </Grid>

      {seismicStandard === 'DIN_EN_1998' ? (
        <Grid item xs={6}>
          <UnitConvertedField
            inputLabel={
              <span>
                S<sub>aP,R</sub>
              </span>
            }
            name="spectral_acceleration"
            type="number"
            unitLabel={
              <span>
                m/s<sup>2</sup>
              </span>
            }
            conversionFactor={1.0}
          />
        </Grid>
      ) : (
        <Grid item xs={6}>
          <Select
            label="Erdbebenzone"
            name="seismic_zone"
            options={seismicStandard === 'DIN_4109' ? seismicZoneOptions : SIA_seismicZoneOptions}
          />
        </Grid>
      )}

      <Grid item xs={6}>
        <TextField inputLabel="q-Verhaltensbeiwert" name="q_value" type="number" />
      </Grid>

      <Grid item xs={12}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Checkbox
            name="use_plateau_value"
            tooltip={{
              text: 'Verwendet den Plateauwert (konstanter Beschleunigungswert zwischen T_B und T_C) unabhängig von der berechneten Schwingungsdauer des Gebäudes.',
            }}
          />
          <Typography fontSize={12}>Nur Plateauwert verwenden</Typography>
        </Stack>
      </Grid>
    </Grid>
  )
}

export default SeismicBaseParameters
